import { forwardRef } from 'react';
import { slowCn } from '../../lib/utils';
import { Box } from '../Box';
import { Image } from '../Image';
import type { SolidFeaturedIconColor } from './styles';
import {
  featuredIconIconVariants,
  featuredIconVariants,
  notificationsFeaturedIconVariants,
  solidFeaturedIconIconVariants,
  solidFeaturedIconVariants,
} from './styles';
import type { FeatureIconProps } from './types';

const FeaturedIcon = forwardRef<HTMLDivElement, FeatureIconProps>(
  function FeaturedIcon(props, ref) {
    const {
      color = 'white',
      size = 'sm',
      Icon,
      alt,
      iconUrl,
      className,
      iconClassName,
      iconStyle,
      ariaLabel,
      IconNode,
      variant = 'default',
      ...rest
    } = props;

    let featuredIconClassName: string;
    let iconDefaultClassName: string;

    if (variant === 'notifications') {
      featuredIconClassName = notificationsFeaturedIconVariants({ className, color, size });
      iconDefaultClassName = featuredIconIconVariants({ color, size });
    } else if (variant === 'solid') {
      featuredIconClassName = solidFeaturedIconVariants({
        className,
        color: color as SolidFeaturedIconColor,
        size,
      });
      iconDefaultClassName = solidFeaturedIconIconVariants({ size });
    } else {
      featuredIconClassName = featuredIconVariants({ className, color, size });
      iconDefaultClassName = featuredIconIconVariants({ color, size });
    }

    return (
      <Box
        aria-label={ariaLabel}
        data-color={color}
        data-component="featured-icon"
        data-size={size}
        {...rest}
        className={slowCn(featuredIconClassName)}
        ref={ref}
      >
        {/* eslint-disable-next-line no-nested-ternary -- is a valid use case */}
        {Icon ? (
          <Icon className={slowCn(iconDefaultClassName, iconClassName)} style={iconStyle} />
        ) : IconNode ? (
          <Box className={slowCn(iconDefaultClassName, iconClassName)} style={iconStyle}>
            {IconNode}
          </Box>
        ) : (
          <Image
            alt={alt}
            className={slowCn(iconDefaultClassName, iconClassName)}
            src={iconUrl}
            style={iconStyle}
          />
        )}
      </Box>
    );
  },
);

export default FeaturedIcon;
